import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { reportService } from '../services/report';
import { getActiveRestaurantId } from '../utils/roleAccess';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Users,
  ShoppingBag,
  DollarSign,
  Clock,
  ChefHat,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import { EmptyState, ButtonLoader, SkeletonTable } from '../components/LoadingStates';

type ReportType =
  | 'order-details'
  | 'dues-details'
  | 'item-wise'
  | 'table-wise'
  | 'daily-closing'
  | 'customer-wise'
  | 'kitchen-wise';

export const Reports: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();

  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const reportTypes = [
    {
      id: 'order-details' as ReportType,
      name: 'Order Details',
      description: 'Detailed list of all settled orders',
      icon: ShoppingBag,
      color: currentTheme.colors.primary[600],
    },
    {
      id: 'dues-details' as ReportType,
      name: 'Dues Details',
      description: 'Outstanding payment details',
      icon: DollarSign,
      color: '#ef4444',
    },
    {
      id: 'item-wise' as ReportType,
      name: 'Item-wise Details',
      description: 'Sales analysis by menu items',
      icon: FileText,
      color: '#10b981',
    },
    {
      id: 'table-wise' as ReportType,
      name: 'Table-wise Details',
      description: 'Revenue analysis by table',
      icon: TrendingUp,
      color: '#f59e0b',
    },
    {
      id: 'daily-closing' as ReportType,
      name: 'Daily Closing',
      description: "Today's end-of-day report",
      icon: Clock,
      color: '#8b5cf6',
    },
    {
      id: 'customer-wise' as ReportType,
      name: 'Customer-wise Details',
      description: 'Customer spending analysis',
      icon: Users,
      color: '#ec4899',
    },
    {
      id: 'kitchen-wise' as ReportType,
      name: 'Kitchen-wise Details',
      description: 'Sales by kitchen/department',
      icon: ChefHat,
      color: '#06b6d4',
    },
  ];

  const activeRestaurantId = getActiveRestaurantId();

  // Fetch report data based on selected report
  const { data: reportData, isLoading } = useQuery(
    ['report', selectedReport, startDate, endDate, user?.organizationCode, activeRestaurantId],
    async () => {
      if (!selectedReport || !user?.organizationCode) return null;

      const filters = {
        org: user.organizationCode,
        start: new Date(startDate + 'T00:00:00Z').toISOString(),
        end: new Date(endDate + 'T23:59:59Z').toISOString(),
        format: 'json' as const,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
      };

      switch (selectedReport) {
        case 'order-details':
          return await reportService.getOrderDetails(filters);
        case 'dues-details':
          return await reportService.getDuesDetails(filters);
        case 'item-wise':
          return await reportService.getItemWiseDetails(filters);
        case 'table-wise':
          return await reportService.getTableWiseDetails(filters);
        case 'daily-closing':
          return await reportService.getDailyClosing(user.organizationCode, undefined, activeRestaurantId);
        case 'customer-wise':
          return await reportService.getCustomerWiseDetails(filters);
        case 'kitchen-wise':
          return await reportService.getKitchenWiseDetails(filters);
        default:
          return null;
      }
    },
    {
      enabled: !!selectedReport && !!user?.organizationCode,
    }
  );

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!selectedReport || !user?.organizationCode) return;

    // Set loading state
    if (format === 'pdf') {
      setIsExportingPdf(true);
    } else {
      setIsExportingExcel(true);
    }

    try {
      const filters = {
        org: user.organizationCode,
        start: new Date(startDate + 'T00:00:00Z').toISOString(),
        end: new Date(endDate + 'T23:59:59Z').toISOString(),
        format,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
      };

      let blob: Blob | any;

      switch (selectedReport) {
        case 'order-details':
          blob = await reportService.getOrderDetails(filters);
          break;
        case 'dues-details':
          blob = await reportService.getDuesDetails(filters);
          break;
        case 'item-wise':
          blob = await reportService.getItemWiseDetails(filters);
          break;
        case 'table-wise':
          blob = await reportService.getTableWiseDetails(filters);
          break;
        case 'daily-closing':
          blob = await reportService.getDailyClosing(user.organizationCode, format, activeRestaurantId);
          break;
        case 'customer-wise':
          blob = await reportService.getCustomerWiseDetails(filters);
          break;
        case 'kitchen-wise':
          blob = await reportService.getKitchenWiseDetails(filters);
          break;
      }

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      // Clear loading state
      if (format === 'pdf') {
        setIsExportingPdf(false);
      } else {
        setIsExportingExcel(false);
      }
    }
  };

  const renderReportContent = () => {
    if (!selectedReport) {
      return (
        <EmptyState
          icon={<BarChart3 className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="Select a Report Type"
          description="Choose a report type from above to view detailed business analytics"
        />
      );
    }

    if (isLoading) {
      return <SkeletonTable rows={8} columns={7} />;
    }

    if (!reportData) {
      return (
        <EmptyState
          title="No Data Available"
          description="There is no data available for the selected date range"
        />
      );
    }

    // Render based on report type
    switch (selectedReport) {
      case 'order-details':
        return renderOrderDetailsReport(reportData as any);
      case 'dues-details':
        return renderDuesDetailsReport(reportData as any);
      case 'item-wise':
        return renderItemWiseReport(reportData as any);
      case 'table-wise':
        return renderTableWiseReport(reportData as any);
      case 'daily-closing':
        return renderDailyClosingReport(reportData as any);
      case 'customer-wise':
        return renderCustomerWiseReport(reportData as any);
      case 'kitchen-wise':
        return renderKitchenWiseReport(reportData as any);
      default:
        return null;
    }
  };

  const renderOrderDetailsReport = (data: any) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalOrders || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalRevenue?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Discount</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalDiscount?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Net Revenue</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.netRevenue?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          <thead className="ui-thead">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Order #</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Table</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Items</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Payment</th>
            </tr>
          </thead>
          <tbody className="ui-tbody">
            {data.orders?.map((order: any) => (
              <tr key={order.orderId} style={{ backgroundColor: currentTheme.colors.background.card }}>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{order.orderNumber}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  {order.orderDate ? format(new Date(order.orderDate), 'MMM dd, yyyy') : '-'}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{order.customerName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{order.tableName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{order.totalItems}</td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{order.total.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{order.paymentMode}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDuesDetailsReport = (data: any) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Dues</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalDues || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Outstanding</p>
          <p className="text-2xl font-bold mt-1 text-red-600">
            ₹{data.summary?.totalOutstanding?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Paid</p>
          <p className="text-2xl font-bold mt-1 text-green-600">
            ₹{data.summary?.totalPaidAmount?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Due Amount</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalDueAmount?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          <thead className="ui-thead">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Order #</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Mobile</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Due Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Days Overdue</th>
            </tr>
          </thead>
          <tbody className="ui-tbody">
            {data.dues?.map((due: any) => (
              <tr key={due.orderId} style={{ backgroundColor: currentTheme.colors.background.card }}>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{due.orderNumber}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{due.customerName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{due.customerMobile}</td>
                <td className="px-4 py-3 text-sm font-medium text-red-600">₹{due.dueAmount.toLocaleString()}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{due.daysOverdue} days</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderItemWiseReport = (data: any) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Items</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalItems || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Quantity Sold</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalQuantity || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalRevenue?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          <thead className="ui-thead">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Item Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Quantity Sold</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Unit Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Revenue</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>%</th>
            </tr>
          </thead>
          <tbody className="ui-tbody">
            {data.items?.map((item: any) => (
              <tr key={item.itemId} style={{ backgroundColor: currentTheme.colors.background.card }}>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{item.itemName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{item.category}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{item.quantitySold}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>₹{item.unitPrice.toLocaleString()}</td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{item.totalRevenue.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{item.percentage.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTableWiseReport = (data: any) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Tables</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalTables || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalOrders || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalRevenue?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          <thead className="ui-thead">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Table Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Avg Order Value</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>%</th>
            </tr>
          </thead>
          <tbody className="ui-tbody">
            {data.tables?.map((table: any) => (
              <tr key={table.tableId} style={{ backgroundColor: currentTheme.colors.background.card }}>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{table.tableName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{table.totalOrders}</td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{table.totalRevenue.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  ₹{table.avgOrderValue.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{table.percentage.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDailyClosingReport = (data: any) => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.sales?.totalOrders || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Gross Sales</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.sales?.grossSales?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Discounts</p>
          <p className="text-2xl font-bold mt-1 text-red-600">
            ₹{data.sales?.discounts?.toLocaleString() || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Net Sales</p>
          <p className="text-2xl font-bold mt-1 text-green-600">
            ₹{data.sales?.netSales?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <h4 className="font-semibold mb-4" style={{ color: currentTheme.colors.text.primary }}>Payment Breakdown</h4>
          <div className="space-y-2">
            {data.paymentBreakdown?.map((payment: any) => (
              <div key={payment.mode} className="flex justify-between items-center">
                <span style={{ color: currentTheme.colors.text.secondary }}>{payment.mode}</span>
                <span className="font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{payment.amount.toLocaleString()} ({payment.count})
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <h4 className="font-semibold mb-4" style={{ color: currentTheme.colors.text.primary }}>Category Sales</h4>
          <div className="space-y-2">
            {data.categorySales?.map((category: any) => (
              <div key={category.category} className="flex justify-between items-center">
                <span style={{ color: currentTheme.colors.text.secondary }}>{category.category}</span>
                <span className="font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{category.revenue.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCustomerWiseReport = (data: any) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Customers</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalCustomers || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalOrders || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalRevenue?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          <thead className="ui-thead">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Customer Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Mobile</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total Spent</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Avg Order Value</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Last Order</th>
            </tr>
          </thead>
          <tbody className="ui-tbody">
            {data.customers?.map((customer: any) => (
              <tr key={customer.customerId} style={{ backgroundColor: currentTheme.colors.background.card }}>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{customer.customerName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{customer.customerMobile}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{customer.totalOrders}</td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{customer.totalSpent.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  ₹{customer.avgOrderValue.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  {customer.lastOrderDate ? format(new Date(customer.lastOrderDate), 'MMM dd, yyyy') : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderKitchenWiseReport = (data: any) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Kitchens</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalKitchens || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            {data.summary?.totalOrders || 0}
          </p>
        </div>
        <div className="p-4 rounded-lg border" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</p>
          <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.colors.text.primary }}>
            ₹{data.summary?.totalRevenue?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          <thead className="ui-thead">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Kitchen Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total Orders</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Items Sold</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>Total Revenue</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>%</th>
            </tr>
          </thead>
          <tbody className="ui-tbody">
            {data.kitchens?.map((kitchen: any) => (
              <tr key={kitchen.kitchenId} style={{ backgroundColor: currentTheme.colors.background.card }}>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{kitchen.kitchenName}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{kitchen.totalOrders}</td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{kitchen.itemsSold}</td>
                <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  ₹{kitchen.totalRevenue.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{kitchen.percentage.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3.5">
          <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
            <BarChart3 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Reports</h1>
            <p className="text-sm sm:text-base mt-1" style={{ color: currentTheme.colors.text.secondary }}>
              Generate and download comprehensive business reports
            </p>
          </div>
        </div>
      </div>

      {/* Report Type Selection */}
      <div
        className="rounded-lg border p-4 mb-6"
        style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}
      >
        <h2 className="text-sm font-semibold mb-4 uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>
          Select Report Type
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            return (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className="p-3 sm:p-4 rounded-lg border text-left transition-all hover:shadow-md"
                style={{
                  backgroundColor: selectedReport === report.id
                    ? currentTheme.colors.background.hover
                    : currentTheme.colors.background.card,
                  borderColor: selectedReport === report.id
                    ? currentTheme.colors.primary[600]
                    : currentTheme.colors.border.primary,
                  borderWidth: selectedReport === report.id ? '2px' : '1px',
                }}
              >
                <Icon className="h-6 w-6 sm:h-8 sm:w-8 mb-2" style={{ color: report.color }} />
                <h3 className="font-semibold text-sm sm:text-base mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  {report.name}
                </h3>
                <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                  {report.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters and Export - Date Range Reports */}
      {selectedReport && selectedReport !== 'daily-closing' && (
        <div
          className="rounded-lg border p-4 mb-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary,
          }}
        >
          <h2 className="text-sm font-semibold mb-4 uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>
            Date Range & Export
          </h2>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                <Calendar className="h-4 w-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded border text-sm sm:text-base"
                style={{
                  backgroundColor: currentTheme.colors.background.secondary,
                  borderColor: currentTheme.colors.border.primary,
                  color: currentTheme.colors.text.primary,
                }}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                <Calendar className="h-4 w-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 rounded border text-sm sm:text-base"
                style={{
                  backgroundColor: currentTheme.colors.background.secondary,
                  borderColor: currentTheme.colors.border.primary,
                  color: currentTheme.colors.text.primary,
                }}
              />
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex gap-2">
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={isExportingPdf || isExportingExcel}
                  className="flex-1 sm:flex-none px-4 py-2 rounded flex items-center justify-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  style={{
                    backgroundColor: currentTheme.colors.primary[600],
                    color: '#ffffff',
                  }}
                >
                  {isExportingPdf ? (
                    <ButtonLoader text="PDF..." />
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      PDF
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleExport('excel')}
                  disabled={isExportingPdf || isExportingExcel}
                  className="flex-1 sm:flex-none px-4 py-2 rounded flex items-center justify-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  style={{
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                  }}
                >
                  {isExportingExcel ? (
                    <ButtonLoader text="Excel..." />
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Export - Daily Closing */}
      {selectedReport === 'daily-closing' && (
        <div
          className="rounded-lg border p-4 mb-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary,
          }}
        >
          <h2 className="text-sm font-semibold mb-4 uppercase tracking-wider" style={{ color: currentTheme.colors.text.secondary }}>
            Export Options
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => handleExport('pdf')}
              disabled={isExportingPdf || isExportingExcel}
              className="flex-1 px-4 py-2 rounded flex items-center justify-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: currentTheme.colors.primary[600],
                color: '#ffffff',
              }}
            >
              {isExportingPdf ? (
                <ButtonLoader text="Exporting..." />
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export PDF
                </>
              )}
            </button>
            <button
              onClick={() => handleExport('excel')}
              disabled={isExportingPdf || isExportingExcel}
              className="flex-1 px-4 py-2 rounded flex items-center justify-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#10b981',
                color: '#ffffff',
              }}
            >
              {isExportingExcel ? (
                <ButtonLoader text="Exporting..." />
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export Excel
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Report Content */}
      <div
        className="rounded-lg border p-4 sm:p-6"
        style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary,
        }}
      >
        {renderReportContent()}
      </div>
    </div>
  );
};
